import { format, isSameDay } from 'date-fns'

import { IotDeviceApi, IotThresholdRange, SensorThreshold, SensorZone } from './types'

export type DeviceStatus = 'online' | 'warning' | 'critical' | 'offline'

export interface SensorMeta {
  iconKey: 'do' | 'ph' | 'temperature' | 'ammonia' | 'turbidity' | 'salinity'
  label: string
  /** Chart heading, when it differs from the card label (e.g. adds the unit). */
  chartLabel?: string
  unit: string
}

export const SENSOR_META: Record<string, SensorMeta> = {
  DO: { iconKey: 'do', label: 'sensor.do', unit: 'ppm' },
  percent_saturation: {
    iconKey: 'do',
    label: 'sensor.percent_saturation',
    chartLabel: 'sensor.percent_saturation_chart',
    unit: '%',
  },
  pH: { iconKey: 'ph', label: 'sensor.ph', unit: '' },
  temperature: { iconKey: 'temperature', label: 'sensor.temperature', unit: '°C' },
  temp: { iconKey: 'temperature', label: 'sensor.temperature', unit: '°C' },
  ammonia: { iconKey: 'ammonia', label: 'sensor.ammonia', unit: 'ppm' },
  Ammonia: { iconKey: 'ammonia', label: 'sensor.ammonia', unit: 'ppm' },
  turbidity: { iconKey: 'turbidity', label: 'sensor.turbidity', unit: 'NTU' },
  Turbidity: { iconKey: 'turbidity', label: 'sensor.turbidity', unit: 'NTU' },
  salinity: { iconKey: 'salinity', label: 'sensor.salinity', unit: 'ppt' },
}

export const DEVICE_TYPE_LABEL: Record<string, string> = {
  'ttn-water-quality': 'device_type.water_quality',
}

export const OFFLINE_MESSAGE = 'device.no_display'

export function getSensorMeta(sensor: string): SensorMeta {
  return SENSOR_META[sensor] ?? { iconKey: 'do', label: sensor, unit: '' }
}

export function getSensorKeys(device: Pick<IotDeviceApi, 'measurements' | 'thresholds'>): string[] {
  const measurementKeys = device.measurements ? Object.keys(device.measurements) : []
  const thresholdKeys = device.thresholds ? Object.keys(device.thresholds) : []
  return Array.from(new Set([...measurementKeys, ...thresholdKeys]))
}

/**
 * Some DO probes don't measure saturation, so the backend sends
 * percent_saturation = null: hide it rather than show "-" (DOF-1372).
 * 0 is a real value and still shows.
 */
export function isUnmeasuredSensor(sensor: string, value: number | null | undefined): boolean {
  return sensor === 'percent_saturation' && value == null
}

/** Chart counterpart of isUnmeasuredSensor: no saturation point in the whole range. */
export function isUnmeasuredHistory(
  sensor: string,
  values: (number | null | undefined)[]
): boolean {
  return sensor === 'percent_saturation' && values.every((v) => v == null)
}

export type SensorValueStatus = 'value' | 'not_plugged' | 'no_data'

export function getSensorValueStatus(
  value: number | null | undefined,
  connectionStatus: IotDeviceApi['device_connection_status']
): SensorValueStatus {
  if (value != null) return 'value'
  return connectionStatus === 'ONLINE' ? 'not_plugged' : 'no_data'
}

export function formatLastUpdate(
  timestamp: string | number | Date,
  now: Date = new Date()
): string {
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return '-'
  return isSameDay(date, now) ? format(date, 'HH:mm') : format(date, 'dd/MM/yyyy HH:mm')
}

export function getDeviceTypeLabel(code: string): string {
  return DEVICE_TYPE_LABEL[code] ?? code
}

export function getDeviceStatus(api: IotDeviceApi): DeviceStatus {
  if (api.device_condition_status === 'CRITICAL') return 'critical'
  if (api.device_condition_status === 'WARNING') return 'warning'
  if (api.device_connection_status === 'OFFLINE') return 'offline'
  return 'online'
}

export function classifyZone(value: number | null | undefined, t: SensorThreshold): SensorZone {
  if (value == null) return 'normal'
  if (value <= t.critLow || value >= t.critHigh) return 'critical'
  if (value <= t.warnLow || value >= t.warnHigh) return 'warning'
  return 'normal'
}

export function toThreshold(
  r: IotThresholdRange | undefined,
  current: number | null | undefined
): SensorThreshold {
  const base = current ?? 0
  if (!r) {
    return {
      critLow: base * 0.5,
      warnLow: base * 0.8,
      warnHigh: base * 1.2,
      critHigh: base * 1.5,
    }
  }
  const critLow = r.critical_low ?? base * 0.5
  const critHigh = r.critical_high ?? base * 1.5
  const span = Math.max(critHigh - critLow, 0.01)
  const warnLow = r.warning_low ?? critLow + span * 0.25
  const warnHigh = r.warning_high ?? critHigh - span * 0.25
  return { critLow, warnLow, warnHigh, critHigh }
}

export type SalinityErrorCode =
  | 'INVALID_SALINITY'
  | 'IOT_SALINITY_NOT_AVAILABLE'
  | 'IOT_DO_PROBE_NOT_FOUND'
  | 'IOT_SALINITY_CHANGED'
  | 'IOT_SALINITY_WRITE_REFUSED'
  | 'IOT_DEVICE_NOT_REPORTING'

const SALINITY_ERROR_CODES: SalinityErrorCode[] = [
  'INVALID_SALINITY',
  'IOT_SALINITY_NOT_AVAILABLE',
  'IOT_DO_PROBE_NOT_FOUND',
  'IOT_SALINITY_CHANGED',
  'IOT_SALINITY_WRITE_REFUSED',
  'IOT_DEVICE_NOT_REPORTING',
]

/**
 * Reads a PATCH /salinity error body (`{ message: "StatusCode 409: CODE", data }`).
 * `code` is null for anything unknown (e.g. 502 — IoT down, nothing changed).
 * `currentSalinity` is only set for IOT_SALINITY_CHANGED: the value really on the
 * probe, which must become the next `expected_salinity` (DOF-1324).
 */
export function parseSalinityError(
  body: { message?: string; data?: { salinity?: number | null } | null } | undefined
): { code: SalinityErrorCode | null; currentSalinity: number | null } {
  const raw = body?.message?.split(':').pop()?.trim() ?? ''
  const code = SALINITY_ERROR_CODES.find((c) => c === raw) ?? null
  const currentSalinity = code === 'IOT_SALINITY_CHANGED' ? body?.data?.salinity ?? null : null
  return { code, currentSalinity }
}
